import { z } from 'zod';

export const QUICK_ACCESS_TOOLBAR_MAX_ITEMS = 18;
export const QUICK_ACCESS_TOOLBAR_MAX_COMMANDS = QUICK_ACCESS_TOOLBAR_MAX_ITEMS;
export const EDITOR_COMMAND_ID_MAX_LENGTH = 80;

export const EditorCommandIdSchema = z
  .string()
  .max(EDITOR_COMMAND_ID_MAX_LENGTH)
  .regex(
    /^[a-z][a-z0-9-]*(?:\.[a-z][a-z0-9-]*)+$/,
    'Command IDs must be stable dot-separated lowercase identifiers'
  );
export type EditorCommandId = z.infer<typeof EditorCommandIdSchema>;

export const QuickAccessToolbarCommandIdsSchema = z
  .array(EditorCommandIdSchema)
  .max(QUICK_ACCESS_TOOLBAR_MAX_ITEMS)
  .superRefine((commandIds, ctx) => {
    if (new Set(commandIds).size !== commandIds.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'Quick Access Toolbar command IDs must be unique',
      });
    }
  });
export type QuickAccessToolbarCommandIds = z.infer<
  typeof QuickAccessToolbarCommandIdsSchema
>;

export const MAC_POWERPOINT_KEY_TIPS_VALUES = [
  'off',
  'option',
  'shift-option',
] as const;
export const MacPowerPointKeyTipsSchema = z.enum(
  MAC_POWERPOINT_KEY_TIPS_VALUES
);
export type MacPowerPointKeyTips = z.infer<typeof MacPowerPointKeyTipsSchema>;
export const MacPowerPointKeyTipActivationSchema = MacPowerPointKeyTipsSchema;
export type MacPowerPointKeyTipActivation = MacPowerPointKeyTips;

export const EditorPreferencesSchema = z.object({
  quickAccessToolbarCommandIds: QuickAccessToolbarCommandIdsSchema.nullable(),
  quickAccessToolbarVisible: z.boolean(),
  macPowerPointKeyTips: MacPowerPointKeyTipsSchema,
  updatedAt: z.string().datetime().nullable(),
});
export type EditorPreferences = z.infer<typeof EditorPreferencesSchema>;
export const EditorPreferencesResponseSchema = EditorPreferencesSchema;
export type EditorPreferencesResponse = EditorPreferences;

export const EditorPreferencesPatchSchema = z
  .object({
    quickAccessToolbarCommandIds:
      QuickAccessToolbarCommandIdsSchema.nullable().optional(),
    quickAccessToolbarVisible: z.boolean().optional(),
    macPowerPointKeyTips: MacPowerPointKeyTipsSchema.optional(),
  })
  .refine(
    (patch) =>
      patch.quickAccessToolbarCommandIds !== undefined ||
      patch.quickAccessToolbarVisible !== undefined ||
      patch.macPowerPointKeyTips !== undefined,
    { message: 'At least one editor preference must be provided' }
  );
export type EditorPreferencesPatch = z.infer<
  typeof EditorPreferencesPatchSchema
>;
export const UpdateEditorPreferencesSchema = EditorPreferencesPatchSchema;
export type UpdateEditorPreferences = EditorPreferencesPatch;
